import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Button, 
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Slider,
    Grid,
    CircularProgress,
    Chip,
    useMediaQuery,
    useTheme,
    FormControlLabel,
    Badge,
    Avatar,
    Tabs,
    Tab,
    AppBar,
    Toolbar,
    Switch,
    Fab,
    Divider,
    Menu,
    MenuItem,
    ListItemAvatar,
    Tooltip,
    ListItemIcon
} from '@mui/material';
import {
    PlayArrow,
    Pause,
    SkipNext,
    SkipPrevious,
    Refresh,
    Visibility,
    VisibilityOff,
    Add,
    Delete,
    Link,
    PowerSettingsNew,
    ContentCopy,
    Check,
    Devices,
    Tv,
    SettingsRemote,
    ZoomIn,
    ZoomOut,
    ZoomOutMap,
    Tab as TabIcon,
    Schedule,
    ArrowBack,
    Close,
    MoreHoriz,
    FiberManualRecord,
    PushPin,
    OpenInNew,
    AccessTime,
    Timer
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { SizeContext, SocketContext, UserContext } from '../../../context/handleContext';
import axios from 'axios';
import { api } from '../../../utils/defaults';
import { toast } from 'react-toastify';
import AnalogScrollControl from './scroll';
import useInterval from 'use-interval';

// Função para formatar o tempo
const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
    return `${Math.floor(seconds / 3600)}h`;
};

// Componentes estilizados
const RemoteContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 500,
  margin: '20px auto',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 24,
  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const ControlButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  height: 48,
  borderRadius: 12,
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1, 2),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
  '&:active': {
    transform: 'translateY(0)',
  }
}));

const NavigationButton = styled(Fab)(({ theme }) => ({
  width: 56,
  height: 56,
  boxShadow: theme.shadows[4],
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[6],
  },
  '&:active': {
    transform: 'scale(0.98)',
  }
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  margin: theme.spacing(0.5),
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& svg': {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  }
}));

const TabListItem = React.memo(({ tab, index, onRemove, onView, onSetTimeout }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    
    const handleMenuClick = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };
  
    const handleMenuClose = () => {
      setAnchorEl(null);
    };
  
    const handleSetTimeout = (time) => {
      onSetTimeout(tab.id, time);
      handleMenuClose();
    };
  
    return (
      <ListItem 
        sx={{ 
          bgcolor: 'background.paper',
          mb: 1,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          borderLeft: tab.active ? `4px solid ${theme.palette.success.main}` : `4px solid ${theme.palette.error.main}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'translateX(4px)',
            boxShadow: theme.shadows[2]
          },
          cursor:'pointer'
        }}
        // onClick={() => onView(tab.id)}
        onClick={(e) => {
            e.stopPropagation();
            onView(tab.id);
        }}
      >
        <ListItemAvatar>
          <Avatar sx={{ 
            bgcolor: tab.active ? 'success.light' : 'error.light',
            color: tab.active ? 'success.contrastText' : 'error.contrastText'
          }}>
            {tab.active ? <Visibility /> : <VisibilityOff />}
          </Avatar>
        </ListItemAvatar>
        
        <ListItemText
          primary={
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                color: tab.isCurrent ? 'success.main' : 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {tab.name || `Aba ${index + 1}`}
              {tab.isCurrent && <FiberManualRecord fontSize="small" sx={{ fontSize: '0.8rem' }} />}
            </Typography>
          }
          secondary={
            <>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '300px'
                }}
              >
                {tab.url}
              </Typography>
              <Box sx={{ display: 'flex', mt: 1, gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`Zoom ${tab.zoom}%`} 
                  size="small" 
                  icon={<ZoomOutMap fontSize="small" />}
                  variant="outlined"
                  color="info"
                />
                <Chip 
                  label={`${formatTime(tab.timeout)}`} 
                  size="small" 
                  icon={<Schedule fontSize="small" />}
                  variant="outlined"
                  color="warning"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClick(e);
                  }}
                />
              </Box>
            </>
          }
        />
        
        <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
          {/* <Tooltip title="Visualizar">
            <IconButton 
              onClick={(e) => {
                e.stopPropagation();
                onView(tab.id);
              }}
              size="small"
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip> */}
          <Tooltip title="Remover">
            <IconButton 
              onClick={(e) => {
                e.stopPropagation();
                onRemove(tab.id);
              }}
              size="small"
              sx={{ '&:hover': { color: 'error.main' } }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
  
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={() => handleSetTimeout(5000)}>
            <ListItemIcon><AccessTime fontSize="small" /></ListItemIcon>
            5 segundos
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(10000)}>
            <ListItemIcon><AccessTime fontSize="small" /></ListItemIcon>
            10 segundos
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(30000)}>
            <ListItemIcon><AccessTime fontSize="small" /></ListItemIcon>
            30 segundos
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(60000)}>
            <ListItemIcon><Timer fontSize="small" /></ListItemIcon>
            1 minuto
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(60000)}>
            <ListItemIcon><Timer fontSize="small" /></ListItemIcon>
            2 minutos
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(300000)}>
            <ListItemIcon><Timer fontSize="small" /></ListItemIcon>
            5 minutos
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(10 * 60 * 1000)}>
            <ListItemIcon><Timer fontSize="small" /></ListItemIcon>
            10 minutos
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(20 * 60 * 1000)}>
            <ListItemIcon><Timer fontSize="small" /></ListItemIcon>
            20 minutos
          </MenuItem>
          <MenuItem onClick={() => handleSetTimeout(30 * 60 * 1000)}>
            <ListItemIcon><Timer fontSize="small" /></ListItemIcon>
            30 minutos
          </MenuItem>
        </Menu>
      </ListItem>
    );
  });

const TimeSlider = ({ value, onChange, label }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ width: '100%', px: 2, py: 1 }}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {label}: {formatTime(value)}
      </Typography>
      <Slider
        value={value}
        onChange={onChange}
        min={1000}
        max={3600000}
        step={1000}
        valueLabelDisplay="auto"
        valueLabelFormat={formatTime}
        sx={{
          color: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.primary.light,
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
            backgroundColor: theme.palette.primary.main,
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 8px ${theme.palette.primary.light}20`,
            },
          },
        }}
      />
    </Box>
  );
};

const ZoomSlider = ({ value, onChange }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ width: 'calc(100% - 30px)', px: 2, py: 1 }}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Zoom: {value}%
      </Typography>
      <Slider
        value={value}
        onChange={onChange}
        min={25}
        max={500}
        step={5}
        marks={[
          { value: 25, label: '25%' },
          { value: 100, label: '100%' },
          { value: 200, label: '200%' },
          { value: 500, label: '500%' },
        ]}
        valueLabelDisplay="auto"
        sx={{
          color: theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.secondary.light,
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
            backgroundColor: theme.palette.secondary.main,
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 8px ${theme.palette.secondary.light}20`,
            },
          },
        }}
      />
    </Box>
  );
};

const RemoteControlPanel = () => {
    const theme = useTheme();
    const [user] = useContext(UserContext);
    const [size] = useContext(SizeContext)
    const socket = useContext(SocketContext);
    
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tabs, setTabs] = useState([]);
    const [rotationStatus, setRotationStatus] = useState(false);
    const [openTabDialog, setOpenTabDialog] = useState(false);
    const [openDeviceDialog, setOpenDeviceDialog] = useState(false);
    const [currentTab, setCurrentTab] = useState({
        url: '',
        name: '',
        timeout: 10000,
        zoom: 100,
        active: true,
        reloadOnOpen: true,
        reloadTime: 0
    });
    const [copied, setCopied] = useState(false);
    const [currentTabId, setCurrentTabId] = useState(null);

    const update = () => {
        const controller = new AbortController()

        setLoading(true)
        axios
        .get(`${api}/api/manage/smartdisplay/device`,{
            headers: {
                Authorization: user.token
            },
            signal: controller.signal
        })
        .then(({ data }) => setDevices( data.filter(({ idtipodisplay, is_used, application }) => (
            idtipodisplay === '25' &&
            is_used &&
            application
        )) ))
        .catch(({response}) => toast.error(response?.data))
        .finally(() => setLoading(false))

        return controller
    }

    const sendCommand = useCallback(async (action, params) => {
        if (!selectedDevice) return;
        
        try {
            await axios.post(
                `${api}/api/manage/smartdisplay/action/browser-command/${selectedDevice.idtabelapreco}`,
                { deviceId: selectedDevice.idtabelapreco, action, params },
                { headers: { Authorization: user.token } }
            );
        } catch (error) {
            console.error('Error sending command:', error);
        }
    }, [
        selectedDevice, 
        user.token
    ]);

    const getData = useCallback(async (action, params = {}) => {
        if (!selectedDevice) return;
        
        try {
            await axios.get(
                `${api}/api/manage/smartdisplay/action/browser-data/${selectedDevice.idtabelapreco}?action=${action}`,
                { headers: { Authorization: user.token } }
            );
        } catch (error) {
            console.error('Error sending command:', error);
        }
    }, [
        selectedDevice, 
        user.token
    ]);

    const removeTab = useCallback((id) => {
        sendCommand('removeTab', { id });
        getData('getTabsInfo')
    }, [currentTabId, sendCommand]);

    const viewTab = useCallback((id) => {
        sendCommand('viewTab', id);
        getData('getTabsInfo')
    }, [currentTabId, sendCommand]);

    const setTimeoutTab = useCallback((id, timeout) => {
        sendCommand('setTimeout', { id, timeout });
        getData('getTabsInfo')
    }, [currentTabId, sendCommand]);

    const setZoom = useCallback((id, zoomLevel) => {
        sendCommand('setZoom', { id, zoomLevel });
    }, [currentTabId, sendCommand]);

    const getZoom = useCallback((id) => {
        sendCommand('getZoom', id);
    }, [currentTabId, sendCommand]);

    const resetZoom = useCallback((id) => {
        sendCommand('resetZoom', id);
    }, [currentTabId, sendCommand]);

    const reloadTab = useCallback((id) => {
        sendCommand('reloadTab', id);
    }, [currentTabId, sendCommand]);

    useEffect(() => {
        const handleBrowserData = ({ action, isRotationEnabled, tabsInfo }) => {
            if (isRotationEnabled!==undefined) setRotationStatus(isRotationEnabled);
            if (tabsInfo) {
                setTabs(tabsInfo)
                setCurrentTabId(tabsInfo.find(({ isCurrent }) => isCurrent)?.id)
            }
        };

        socket.on('browser-data', handleBrowserData);
        return () => {
            socket.off('browser-data', handleBrowserData);
        };
    }, [socket]);

    const handleAddTab = () => {
        setCurrentTab({
            url: '',
            name: '',
            timeout: 10000,
            zoom: 100,
            active: true,
            reloadOnOpen: true,
            reloadTime: 0
        });
        setOpenTabDialog(true);
    };

    const handleSaveTab = () => {
        sendCommand('addTab', currentTab);
        setOpenTabDialog(false);
    };

    const handleCopyDeviceId = () => {
        navigator.clipboard.writeText(selectedDevice.key_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Componente de controles de navegação
    const NavigationControls = useMemo(() => (
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            alignItems: 'center',
            mb: 3,
            position: 'relative'
        }}>
            <NavigationButton 
            color="primary" 
            sx={{ mr: 4 }}
            onClick={() => sendCommand('prevTab')}
            >
                <SkipPrevious fontSize="large" />
            </NavigationButton>
            
            <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1
            }}>
            <Fab 
                color={rotationStatus ? 'secondary' : 'primary'} 
                size="medium"
                onClick={() => {
                    rotationStatus ? sendCommand('stopRotation') : sendCommand('startRotation');
                    // setRotationStatus(!rotationStatus);
                    getData('isRotationEnabled')
                }}
                sx={{
                width: 64,
                height: 64,
                '&:hover': {
                    transform: 'scale(1.1)',
                }
                }}
            >
                {rotationStatus ? <Pause fontSize="large" /> : <PlayArrow fontSize="large" />}
            </Fab>
            </Box>
            
            <NavigationButton 
            color="primary" 
            sx={{ ml: 4 }}
            onClick={() => sendCommand('nextTab')}
            >
            <SkipNext fontSize="large" />
            </NavigationButton>
        </Box>
    ), [
        rotationStatus, 
        sendCommand
    ]);

    // Componente de controles de zoom
    const ZoomControls = useMemo(() => (
        <Box sx={{ mb: 3 }}>
            <ZoomSlider 
            value={currentTab.zoom}
            onChange={(e, value) => {
                setCurrentTab({...currentTab, zoom: value})
                setZoom(undefined, value)
            }}
            
            />
            <Box 
            sx={{ display: 'flex', justifyContent: 'space-around', gap: 2, mt: 1, alignItems:'center' }}>
                <NavigationButton 
                color="primary" 
                // sx={{ mr: 4 }}
                onClick={() => {
                    setCurrentTab({...currentTab, zoom: currentTab.zoom - 10})
                    setZoom(undefined, currentTab.zoom - 10 )
                }}
                >
                    <ZoomOut />
                </NavigationButton>
                <ControlButton
                    variant="outlined"
                    startIcon={<ZoomOutMap />}
                    onClick={() => {
                        setCurrentTab({...currentTab, zoom: 100 })
                        setZoom(undefined, 100 )
                    }}
                >
                    Resetar
                </ControlButton>
                <NavigationButton 
                color="primary" 
                // sx={{ mr: 4 }}
                onClick={() => {
                    setCurrentTab({...currentTab, zoom: currentTab.zoom + 10})
                    setZoom(undefined, currentTab.zoom + 10 )
                }}
                >
                    <ZoomIn />
                </NavigationButton>
            </Box>
        </Box>
    ), [
        currentTab.zoom, 
        setZoom, 
        resetZoom
    ]);

    // Componente de ações rápidas
    const QuickActions = useMemo(() => (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <ControlButton
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={() => reloadTab(undefined)}
          >
            Recarregar
          </ControlButton>
        </Grid>
        
        <Grid item xs={6}>
          <ControlButton
            fullWidth
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={handleAddTab}
          >
            Nova Aba
          </ControlButton>
        </Grid>
        
        <Grid item xs={12}>
          <ControlButton
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<PowerSettingsNew />}
            onClick={() => sendCommand('restartBrowser')}
          >
            Reiniciar Navegador
          </ControlButton>
        </Grid>
      </Grid>
    ), [
        reloadTab, 
        handleAddTab, 
        sendCommand
    ]);

    useEffect(() => {
      if ( !selectedDevice ) return 
      getData('getTabsInfo')
      getData('isRotationEnabled')
    },[selectedDevice])

    useInterval(() => {
        if ( !selectedDevice ) return
        getData('getTabsInfo')
        getData('isRotationEnabled')
    }, 10 * 1000)

    return (
        <div 
        style={{
            height: 'calc(100% - 64px)',
            maxHeight: 'calc(100% - 64px)',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems:'center',
            overflowY:'hidden'
        }}>
            <RemoteContainer 
                elevation={3}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', md: '1200px' },
                    padding: { xs: '8px', sm: '16px' },
                    paddingTop:'16px !important',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    display: { xs: 'block', md: 'flex' },
                    gap: 3,
                    height:'fit-content',
                    maxHeight:'calc(100% - 64px)'
                }}
            >
                {/* Conteúdo principal (esquerda) */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    minWidth: 0,
                    maxWidth: { xs: '100%', md: '650px' }
                }}>
                    {/* Cabeçalho */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: { xs: 2, md: 0 },
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <SettingsRemote 
                                sx={{ 
                                    fontSize: 32,
                                    color: theme.palette.primary.main,
                                    mr: 1
                                }} 
                            />
                            <Button onClick={() => {
                                update()
                                setOpenDeviceDialog(true)
                            }}>
                                Selecionar
                            </Button>
                        </Box>
                        
                        {selectedDevice && (
                            <Chip
                                avatar={<Avatar sx={{ bgcolor: theme.palette.primary.main }}><Tv fontSize="small" /></Avatar>}
                                label={(selectedDevice.apelido || `${selectedDevice.loja || 'Sem loja'}, ${selectedDevice.area_venda || 'Sem área de venda'}`)}
                                color="primary"
                                onDelete={handleCopyDeviceId}
                                deleteIcon={copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                sx={{ fontWeight: 500, maxWidth: '100%' }}
                            />
                        )}
                    </Box>
        
                    {/* Grid de controles */}
                    <Box sx={{
                        display: { xs: 'block', md: 'grid' },
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 3
                    }}>
                        {/* Coluna 1 */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Área de navegação */}
                            <Paper sx={{ 
                                p: 2, 
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                flex: 1,
                                mb: { xs: 2, md: 0 }
                            }}>
                                <SectionTitle variant="subtitle1">
                                    <SettingsRemote /> Controles de Navegação
                                </SectionTitle>
                                {NavigationControls}
                            </Paper>

                            {/* Controle analógico */}
                            <Paper sx={{ 
                                p: 2, 
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                justifyContent: 'center',
                                minHeight: '200px',
                                mb: { xs: 2, md: 0 }
                            }}>
                                <AnalogScrollControl onScroll={data => sendCommand('scrollView', data)} />
                            </Paper>
        
                            
                        </Box>
        
                        {/* Coluna 2 */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Ações rápidas */}
                            <Paper sx={{ 
                                p: 2, 
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                flex: 1,
                                mb: { xs: 2, md: 0 }
                            }}>
                                <SectionTitle variant="subtitle1">
                                    <MoreHoriz /> Ações Rápidas
                                </SectionTitle>
                                {QuickActions}
                            </Paper>
        
                            {/* Área de zoom */}
                            <Paper sx={{ 
                                p: 2, 
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                flex: 1,
                                mb: { xs: 2, md: 0 }
                            }}>
                                <SectionTitle variant="subtitle1">
                                    <ZoomOutMap /> Controles de Zoom
                                </SectionTitle>
                                {ZoomControls}
                            </Paper>
                        </Box>
                    </Box>
                </Box>
        
                {/* Lista de abas (direita - apenas desktop) */}
                <Box 
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: '100%',
                    maxWidth: `${(size.width >= 1200 ? 1200 : size.width) - 680}px !important`,
                    height: '100%',
                    overflowY: 'auto',
                    position: 'sticky',
                    top: 0
                }}>
                    <Paper sx={{ 
                        p: 2,
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        height: 'calc(100% - 40px)'
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            mb: 2 
                        }}>
                            <SectionTitle variant="subtitle1">
                                <TabIcon /> Abas ({tabs.length})
                            </SectionTitle>
                            <ControlButton
                                variant="contained"
                                size="small"
                                startIcon={<Add />}
                                onClick={handleAddTab}
                            >
                                Adicionar
                            </ControlButton>
                        </Box>
                        
                        {tabs.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ p: 2 }}>
                                Nenhuma aba configurada. Clique em "Adicionar" para criar uma nova aba.
                            </Typography>
                        ) : (
                            <List dense>
                                {tabs.map((tab, index) => (
                                    <TabListItem 
                                        key={tab.id} 
                                        tab={tab}
                                        index={index}
                                        onRemove={removeTab}
                                        onView={viewTab}
                                        onSetTimeout={setTimeoutTab}
                                    />
                                ))}
                            </List>
                        )}
                    </Paper>
                </Box>
        
                {/* Lista de abas (mobile) */}
                <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
                    <Paper sx={{ 
                        p: 2,
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            mb: 2 
                        }}>
                            <SectionTitle variant="subtitle1">
                                <TabIcon /> Abas ({tabs.length})
                            </SectionTitle>
                            <ControlButton
                                variant="contained"
                                size="small"
                                startIcon={<Add />}
                                onClick={handleAddTab}
                            >
                                Adicionar
                            </ControlButton>
                        </Box>
                        
                        {tabs.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ p: 2 }}>
                                Nenhuma aba configurada. Clique em "Adicionar" para criar uma nova aba.
                            </Typography>
                        ) : (
                            <List dense>
                                {tabs.map((tab, index) => (
                                    <TabListItem 
                                        key={tab.id} 
                                        tab={tab}
                                        index={index}
                                        onRemove={removeTab}
                                        onView={viewTab}
                                        onSetTimeout={setTimeoutTab}
                                    />
                                ))}
                            </List>
                        )}
                    </Paper>
                </Box>
            </RemoteContainer>

            <Dialog 
            open={openTabDialog} 
            onClose={() => setOpenTabDialog(false)} 
            fullWidth 
            maxWidth="sm"
            PaperProps={{
                sx: {
                borderRadius: 3,
                overflow: 'hidden'
                }
            }}
            >
            <DialogTitle sx={{ 
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TabIcon sx={{ mr: 1 }} />
                {currentTab.id ? 'Editar Aba' : 'Nova Aba'}
                </Box>
                <IconButton onClick={() => setOpenTabDialog(false)} sx={{ color: 'inherit' }}>
                <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
                <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Nome da Aba"
                    value={currentTab.name}
                    onChange={(e) => setCurrentTab({...currentTab, name: e.target.value})}
                    variant="outlined"
                    margin="normal"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="URL"
                    value={currentTab.url}
                    onChange={(e) => setCurrentTab({...currentTab, url: e.target.value})}
                    variant="outlined"
                    margin="normal"
                    placeholder="https://"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TimeSlider 
                    value={currentTab.timeout}
                    onChange={(e, value) => setCurrentTab({...currentTab, timeout: value})}
                    label="Tempo de Exibição"
                    />
                </Grid>
                <Grid item xs={12}>
                    <ZoomSlider 
                    value={currentTab.zoom}
                    onChange={(e, value) => setCurrentTab({...currentTab, zoom: value})}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                    control={
                        <Switch
                        checked={currentTab.active}
                        onChange={(e) => setCurrentTab({...currentTab, active: e.target.checked})}
                        color="primary"
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {currentTab.active ? 
                            <Visibility color="success" sx={{ mr: 1 }} /> : 
                            <VisibilityOff color="error" sx={{ mr: 1 }} />}
                        <Typography>
                            Aba {currentTab.active ? 'Ativa' : 'Inativa'}
                        </Typography>
                        </Box>
                    }
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                    control={
                        <Switch
                        checked={currentTab.reloadOnOpen}
                        onChange={(e) => setCurrentTab({...currentTab, reloadOnOpen: e.target.checked})}
                        color="secondary"
                        />
                    }
                    label="Recarregar ao abrir"
                    />
                </Grid>
                {!currentTab.reloadOnOpen && (
                    <Grid item xs={12}>
                    <TimeSlider 
                        value={currentTab.reloadTime}
                        onChange={(e, value) => setCurrentTab({...currentTab, reloadTime: value})}
                        label="Recarregar a cada"
                    />
                    </Grid>
                )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button 
                onClick={() => setOpenTabDialog(false)}
                variant="outlined"
                startIcon={<Close />}
                >
                Cancelar
                </Button>
                <Button 
                onClick={handleSaveTab} 
                variant="contained" 
                color="primary"
                disabled={!currentTab.url}
                startIcon={<Check />}
                >
                Salvar Configurações
                </Button>
            </DialogActions>
            </Dialog>

            {/* Diálogo para seleção de dispositivo */}
            <Dialog 
            open={openDeviceDialog} 
            onClose={() => setOpenDeviceDialog(false)}
            PaperProps={{
                sx: {
                borderRadius: 3,
                overflow: 'hidden'
                }
            }}
            >
            <DialogTitle sx={{ 
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Devices sx={{ mr: 1 }} />
                Selecione um Dispositivo
                </Box>
                <IconButton onClick={() => setOpenDeviceDialog(false)} sx={{ color: 'inherit' }}>
                <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
                ) : (
                <List>
                    {devices.map(device => (
                    <ListItem 
                        key={device.id}
                        button
                        selected={selectedDevice?.id === device.id}
                        onClick={() => {
                        setSelectedDevice(device);
                        setOpenDeviceDialog(false);
                        }}
                        sx={{
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.05)',
                        },
                        '&.Mui-selected': {
                            backgroundColor: `${theme.palette.primary.light}20`,
                        }
                        }}
                    >
                        <ListItemText 
                        primary={
                            <Typography fontWeight={500}>
                            {device.apelido || `${device.loja || 'Sem loja'}, ${device.area_venda || 'Sem área de venda'}`}
                            </Typography>
                        } 
                        secondary={`ID: ${device.idtabelapreco}`} 
                        />
                    </ListItem>
                    ))}
                </List>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button 
                onClick={() => setOpenDeviceDialog(false)}
                variant="outlined"
                startIcon={<ArrowBack />}
                >
                Voltar
                </Button>
            </DialogActions>
            </Dialog>
        </div>
    );
};

export default React.memo(RemoteControlPanel);